import {
  getCurrentAgreementApproval,
  getAgreementApprovals,
} from "../../../../api/agreementsApi";

/** Статусы процесса соглашения в списке */
export const AGREEMENT_LIST_STATUS_LABELS = {
  draft: "Черновик",
  pending: "На согласовании",
  on_approval: "На согласовании",
  approved: "Согласовано",
  rejected: "Отклонено",
  active: "Активно",
  closed: "Закрыто",
  paid_advance: "Оплачен Аванс",
  overdue_advance: "Просрочен Аванс",
  expected_ODGE: "Ожидается ОДГЭ",
  expected_OPGE: "Ожидается ОПГЭ",
  overdue_OPGE: "Просрочен ОПГЭ",
};

export const APPROVAL_STATUS_LABELS = {
  pending: "На согласовании",
  approved: "Согласовано",
  signed: "Согласовано",
  rejected: "Отклонено",
};

export const APPROVAL_STEP_STATUS_LABELS = {
  pending: "Ожидает",
  approved: "Согласован",
  signed: "Согласован",
  rejected: "Отклонён",
};

const DELETABLE_LIST_STATUSES = new Set(["pending", "approved"]);

export const AGREEMENT_STATUSES_WITH_APPROVAL_PROCESS = new Set([
  "pending",
  "on_approval",
  "rejected",
  "approved",
  "signed",
]);

const pickLatestApprovalFromHistory = (history) => {
  const list = Array.isArray(history) ? history : [];
  if (list.length === 0) return null;

  const withId = [...list].reverse().find((item) => item?.id != null);
  return withId ?? list[list.length - 1];
};

const resolveApprovalIdFromAgreement = (agreement) =>
  agreement?.approval_id ??
  agreement?.last_approval_id ??
  agreement?.approval_summary?.id ??
  agreement?.approval_summary?.approval_id ??
  agreement?.currentApproval?.id ??
  agreement?.latestApproval?.id ??
  null;

export const resolveApprovalInitiatorId = (approval, agreement) =>
  approval?.initiator_id ??
  agreement?.approval_summary?.initiator_id ??
  agreement?.currentApproval?.initiator_id ??
  agreement?.latestApproval?.initiator_id ??
  null;

export const resolveApprovalInitiatorName = (approval, agreement) =>
  approval?.initiator_name ??
  agreement?.approval_summary?.initiator_name ??
  agreement?.currentApproval?.initiator_name ??
  agreement?.latestApproval?.initiator_name ??
  null;

export const isAgreementApprovalInitiator = (approval, agreement, userId) => {
  if (userId == null) return false;
  const initiatorId = resolveApprovalInitiatorId(approval, agreement);
  if (initiatorId == null) return false;
  return String(initiatorId) === String(userId);
};

/** Статус для колонки списка — из agreement.status */
export const resolveAgreementListStatus = (agreement) => {
  const status = agreement?.status;

  if (status === "pending" || status === "on_approval") return "pending";
  if (status === "approved" || status === "signed") return "approved";
  if (status === "rejected") return "rejected";
  if (status === "draft" || status === "active" || status == null) {
    return "draft";
  }

  return status;
};

/** Процесс с id — если уже есть в объекте соглашения */
export const getManageableAgreementApproval = (agreement) => {
  const approvalId = resolveApprovalIdFromAgreement(agreement);
  if (approvalId == null) return null;

  return {
    id: approvalId,
    status:
      agreement?.currentApproval?.status ??
      agreement?.latestApproval?.status ??
      agreement?.approval_summary?.status ??
      agreement?.status,
  };
};

/**
 * Ленивая подгрузка approval_id — только по действию пользователя
 * (отмена/удаление согласования).
 */
export const resolveAgreementApprovalId = async (agreement) => {
  const embedded = resolveApprovalIdFromAgreement(agreement);
  if (embedded != null) return embedded;

  const currentApproval = await getCurrentAgreementApproval(agreement.id);
  if (currentApproval?.id != null) return currentApproval.id;

  const history = await getAgreementApprovals(agreement.id);
  return pickLatestApprovalFromHistory(history)?.id ?? null;
};

export const canSendAgreementForApproval = (agreement) => {
  const status = resolveAgreementListStatus(agreement);
  return status === "draft" || status === "rejected";
};

export const getSendForApprovalTitle = (agreement) =>
  resolveAgreementListStatus(agreement) === "rejected"
    ? "Отправить на согласование повторно"
    : "Отправить на согласование";

/** Показывать кнопку ↩ только инициатору при активном процессе согласования */
export const canShowDeleteApprovalAction = (
  agreement,
  userId,
  approval = null,
) => {
  const status = resolveAgreementListStatus(agreement);
  if (!DELETABLE_LIST_STATUSES.has(status)) return false;
  const hasApproval =
    approval?.id != null || resolveApprovalIdFromAgreement(agreement) != null;
  if (!hasApproval) return false;
  return isAgreementApprovalInitiator(approval, agreement, userId);
};

export const mergeAgreementApprovalSummary = (agreement, approval) => {
  if (!agreement || approval?.initiator_id == null) return agreement;
  return {
    ...agreement,
    approval_summary: {
      ...(agreement.approval_summary ?? {}),
      id: approval.id ?? agreement.approval_id ?? agreement.approval_summary?.id,
      status: approval.status ?? agreement.approval_summary?.status,
      initiator_id: approval.initiator_id,
      initiator_name: approval.initiator_name,
    },
  };
};

/** Для списка: подтянуть initiator_id из GET /approval, если в списке его нет */
export const enrichAgreementsWithApprovalInitiators = async (agreements) => {
  const list = Array.isArray(agreements) ? agreements : [];
  const needsEnrichment = list.filter(
    (agreement) =>
      agreement?.approval_id != null &&
      resolveApprovalInitiatorId(null, agreement) == null &&
      DELETABLE_LIST_STATUSES.has(resolveAgreementListStatus(agreement)),
  );
  if (needsEnrichment.length === 0) return list;

  const enrichedById = new Map(
    (
      await Promise.all(
        needsEnrichment.map(async (agreement) => {
          try {
            const approval = await getCurrentAgreementApproval(agreement.id);
            return [agreement.id, mergeAgreementApprovalSummary(agreement, approval)];
          } catch {
            return [agreement.id, agreement];
          }
        }),
      )
    ).map(([id, item]) => [id, item]),
  );

  return list.map((agreement) => enrichedById.get(agreement.id) ?? agreement);
};

export const getDeleteApprovalTitle = (agreement) => {
  const status = resolveAgreementListStatus(agreement);

  if (status === "pending") return "Отменить согласование";
  if (status === "approved") return "Удалить согласованное согласование";
  return "Удалить процесс согласования";
};

export const getAgreementListStatusBadgeClass = (status) => {
  if (!status) return "status-badge";
  if (status === "draft" || status === "closed")
    return "status-badge status-badge--inactive";
  if (status === "pending") return "status-badge status-badge--expected";
  if (status === "approved" || status === "signed")
    return "status-badge status-badge--paid";
  if (status === "rejected") return "status-badge status-badge--overdue";
  if (status === "active") return "status-badge status-badge--inactive";
  if (status.includes("paid") && !status.includes("overdue"))
    return "status-badge status-badge--paid";
  if (status.includes("overdue")) return "status-badge status-badge--overdue";
  if (status.includes("expected"))
    return "status-badge status-badge--expected";
  return "status-badge status-badge--expected";
};

export { pickLatestApprovalFromHistory };
